import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import PageStructure from 'flarum/forum/components/PageStructure';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import Link from 'flarum/common/components/Link';
import Icon from 'flarum/common/components/Icon';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Button from 'flarum/common/components/Button';
import Select from 'flarum/common/components/Select';
import extractText from 'flarum/common/utils/extractText';
import { TopicHeatFlames, TopicHeatTrend } from './TopicHeat';
import RelatedArticles from './RelatedArticles';

/**
 * The orders the topics can be listed in.
 *
 * The names are the sort keys the API understands; the label is the
 * translation key the dropdown shows.
 */
const SORTS = {
  heat: {
    column: '-heatScore',
    label: 'ziiven-topics.forum.browse.sort_heat'
  },
  trend: {
    column: '-heatTrend',
    label: 'ziiven-topics.forum.browse.sort_trend'
  },
  recent: {
    column: '-lastUsedAt',
    label: 'ziiven-topics.forum.browse.sort_recent'
  },
  name: {
    column: 'name',
    label: 'ziiven-topics.forum.browse.sort_name'
  }
};

/**
 * How many topics each group shows.
 */
const GROUP_SIZE = 5;

/**
 * The icon that marks each group.
 *
 * The label itself comes from the locale file, so the icon is chosen here —
 * the group is picked by its measure, and each measure has its own arrow.
 */
const GROUP_ICONS = {
  heat: 'fas fa-fire',
  trend: 'fas fa-arrow-up',
  recent: 'fas fa-clock'
};

/**
 * `/topics` — every topic anybody has used.
 *
 * The page is a browse page, not a feed: it is here to answer "has anybody
 * written about this already?" and "what is going on in the forum right
 * now?". So it shows the topics three times over, each time by the measure
 * that matters for that question — the hottest, the fastest rising, and the
 * most recently used.
 */
export default class TopicsPage extends Page {
  topics = [];

  /** How many topics exist in total, which is not the same as how many we loaded. */
  total = 0;
  loading = true;

  /** The order the groups are listed in. */
  sort = 'heat';

  /** The search term. */
  query = '';

  /** Whether topics without a single discussion are hidden. */
  onlyWithDiscussions = false;

  /** Guards against an earlier slow response overwriting a later fast one. */
  requestId = 0;

  /** The search box waits for a pause in typing. */

  oninit(vnode) {
    super.oninit(vnode);
    app.setTitle(extractText(app.translator.trans('ziiven-topics.forum.browse.title')));
    this.load();
  }
  onremove(vnode) {
    super.onremove(vnode);
    if (this.searchTimer) window.clearTimeout(this.searchTimer);
  }

  /**
   * Load the topics.
   *
   * The whole set is fetched once, sorted by heat, and the three groups are
   * worked out from it. Asking the API for three differently sorted lists
   * would mean three round trips for the same rows.
   */
  async load() {
    const id = ++this.requestId;
    this.loading = true;
    m.redraw();
    try {
      const params = {
        sort: SORTS.heat.column,
        'page[limit]': '100'
      };
      if (this.query) params['filter[q]'] = this.query;
      const payload = await app.request({
        method: 'GET',
        url: `${app.forum.attribute('apiUrl')}/topics`,
        params
      });

      // A stale response — the user has typed or re-sorted since. Dropping it
      // is what stops the list flickering back to previous results.
      if (id !== this.requestId) return;
      const models = app.store.pushPayload(payload);
      this.topics = Array.isArray(models) ? models : [];
      this.total = payload?.meta?.page?.total ?? this.topics.length;
    } catch {
      if (id !== this.requestId) return;
      this.topics = [];
      this.total = 0;
    }
    if (id !== this.requestId) return;
    this.loading = false;
    m.redraw();
  }

  /**
   * The topics that survive the search and the filter.
   */
  visibleTopics() {
    let topics = this.topics;
    if (this.onlyWithDiscussions) {
      topics = topics.filter(topic => topic.discussionCount() > 0);
    }
    return topics;
  }

  /**
   * Sort by the chosen order.
   */
  order(topics) {
    const sorted = topics.slice();
    switch (this.sort) {
      case 'trend':
        sorted.sort((a, b) => (b.heatTrend() ?? -Infinity) - (a.heatTrend() ?? -Infinity));
        break;
      case 'recent':
        sorted.sort((a, b) => (b.lastUsedAt()?.getTime() ?? 0) - (a.lastUsedAt()?.getTime() ?? 0));
        break;
      case 'name':
        sorted.sort((a, b) => a.name().localeCompare(b.name()));
        break;
      default:
        sorted.sort((a, b) => b.heatScore() - a.heatScore());
        break;
    }
    return sorted;
  }
  view() {
    return m(PageStructure, {
      className: "TopicsPage Page--vertical",
      sidebar: this.sidebar.bind(this)
    }, this.content());
  }
  sidebar() {
    return m(IndexSidebar, null);
  }
  content() {
    return m("div", {
      className: "TopicsPage-content"
    }, m("h1", {
      className: "TopicsPage-title"
    }, app.translator.trans('ziiven-topics.forum.browse.title')), m("p", {
      className: "TopicsPage-lede"
    }, app.translator.trans('ziiven-topics.forum.browse.lede', {
      count: this.total
    })), m("div", {
      className: "TopicsPage-controls"
    }, m("input", {
      className: "FormControl TopicsPage-search",
      type: "search",
      placeholder: extractText(app.translator.trans('ziiven-topics.forum.browse.search_placeholder')),
      value: this.query,
      oninput: e => {
        this.query = e.target.value.trim();
        if (this.searchTimer) window.clearTimeout(this.searchTimer);
        this.searchTimer = window.setTimeout(() => this.load(), 300);
      }
    }), this.sortControl(), m(Button, {
      className: 'Button Button--link TopicsPage-toggle' + (this.onlyWithDiscussions ? ' active' : ''),
      onclick: () => {
        this.onlyWithDiscussions = !this.onlyWithDiscussions;
        m.redraw();
      }
    }, app.translator.trans('ziiven-topics.forum.browse.only_with_discussions'))), this.loading ? m(LoadingIndicator, null) : this.visibleTopics().length === 0 ? m("p", {
      className: "TopicsPage-empty"
    }, app.translator.trans(this.query ? 'ziiven-topics.forum.browse.no_matches' : 'ziiven-topics.forum.browse.empty')) : m("div", {
      className: "TopicsPage-groups"
    }, this.group('hottest', 'ziiven-topics.forum.browse.group_hottest', 'heat'), this.group('rising', 'ziiven-topics.forum.browse.group_rising', 'trend'), this.group('recent', 'ziiven-topics.forum.browse.group_recent', 'recent')), m(RelatedArticles, null));
  }

  /**
   * One group of topics.
   *
   * `criterion` is the measure the group is picked by; the chosen order
   * decides how the picked topics are then listed.
   */
  group(name, label, criterion) {
    const topics = this.pick(criterion).slice(0, GROUP_SIZE);
    if (!topics.length) return null;
    return m("div", {
      className: 'TopicsPage-group TopicsPage-group--' + name
    }, m("b", {
      className: "TopicsPage-groupTitle"
    }, m(Icon, {
      name: GROUP_ICONS[criterion]
    }), " ", app.translator.trans(label)), m("div", {
      className: "TopicsPage-cloud"
    }, topics.map(topic => m(Link, {
      className: "taglink TopicsPage-topic",
      href: app.route('topic', {
        name: topic.nameKey()
      }),
      key: topic.id()
    }, "#", topic.name(), criterion === 'heat' ? m(TopicHeatFlames, {
      level: topic.heatLevel()
    }) : null, criterion === 'trend' ? m(TopicHeatTrend, {
      trend: topic.heatTrend()
    }) : null))));
  }

  /**
   * The topics of one group, before they are listed in the chosen order.
   */
  pick(criterion) {
    const topics = this.visibleTopics();
    if (criterion === 'heat') {
      return this.order(topics).slice(0, GROUP_SIZE);
    }
    if (criterion === 'trend') {
      return topics.filter(topic => topic.heatTrend() !== null).sort((a, b) => (b.heatTrend() ?? 0) - (a.heatTrend() ?? 0)).slice(0, GROUP_SIZE);
    }
    return topics.slice().sort((a, b) => (b.lastUsedAt()?.getTime() ?? 0) - (a.lastUsedAt()?.getTime() ?? 0)).slice(0, GROUP_SIZE);
  }

  /**
   * The order the topics are listed in.
   *
   * A select, the way the design draws it: it is a filter, not an action, and
   * on a phone a native picker beats a dropdown menu.
   */
  sortControl() {
    const options = {};
    Object.keys(SORTS).forEach(key => {
      options[key] = String(app.translator.trans(SORTS[key].label));
    });
    return m(Select, {
      className: "TopicsPage-sort",
      value: this.sort,
      options: options,
      onchange: value => {
        if (this.sort === value) return;
        this.sort = value;
        m.redraw();
      }
    });
  }
}
flarum.reg.add('ziiven-topics', 'forum/components/TopicsPage', TopicsPage);